import type { ReactNode, FC } from "react";
import {
  useMemo,
  createContext,
  useState,
  useContext,
  useCallback,
} from "react";

import { DesktopPopup } from "tw/components/molecules/DesktopPopup";
import { MobileBottomDrawer } from "tw/components/molecules/MobileBottomDrawer";
import type {
  BodyProps,
  TitleProps,
} from "tw/components/molecules/SettingsPopupContent/inject";

type SettingsPopupContextType = {
  openSettingsPopup: (
    title: TitleProps | undefined,
    body: BodyProps,
    openOnRight?: boolean
  ) => void;
  closeSettingsPopup: () => void;
  updateSettingsPopup: ({
    title,
    body,
  }: {
    title?: Partial<TitleProps>;
    body?: Partial<BodyProps>;
  }) => void;
};

const SettingsPopupContext = createContext<SettingsPopupContextType>({
  openSettingsPopup: () => {},
  closeSettingsPopup: () => {},
  updateSettingsPopup: () => {},
});

export const SettingsPopupProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isSettingsPopupOpen, setIsSettingsPopupOpen] = useState(false);
  const [popupTitle, setPopupTitle] = useState<TitleProps>();
  const [popupBody, setPopupBody] = useState<BodyProps>();
  const [isOnRight, setIsOnRight] = useState<boolean>();

  const openSettingsPopup = useCallback(
    (title: TitleProps | undefined, body: BodyProps, openOnRight?: boolean) => {
      setPopupTitle(title);
      setPopupBody(body);
      setIsOnRight(openOnRight);
      setIsSettingsPopupOpen(true);
    },
    []
  );

  const closeSettingsPopup = useCallback(() => {
    setIsSettingsPopupOpen(false);
  }, []);

  const updateSettingsPopup = useCallback(
    ({
      title,
      body,
    }: {
      title?: Partial<TitleProps>;
      body?: Partial<BodyProps>;
    }) => {
      if (title) {
        setPopupTitle((cur) => ({ ...cur, ...title }));
      }
      if (body) {
        setPopupBody((cur) => ({ ...cur, ...body }));
      }
    },
    []
  );

  const contextValues = useMemo(
    () => ({
      openSettingsPopup,
      closeSettingsPopup,
      updateSettingsPopup,
    }),
    [openSettingsPopup, closeSettingsPopup, updateSettingsPopup]
  );

  return (
    <SettingsPopupContext.Provider value={contextValues}>
      {children}
      <>
        <DesktopPopup
          title={popupTitle}
          body={popupBody}
          open={isSettingsPopupOpen}
          openOnRight={isOnRight}
          onClose={() => setIsSettingsPopupOpen(false)}
          width={popupBody?.steps ? "wide" : "normal"}
        />
        <MobileBottomDrawer
          title={popupTitle}
          body={popupBody}
          open={isSettingsPopupOpen}
          onClose={() => setIsSettingsPopupOpen(false)}
        />
      </>
    </SettingsPopupContext.Provider>
  );
};

export const useSettingsPopup = () => useContext(SettingsPopupContext);
